import {Lang, Language} from 'yougile-lang'
import {DataObjectId} from './data-object'
import {JSONLikeValue} from './diff'
import {HubJSON} from './hubs'

export const isValidExtensionRating = (
  value: unknown
): value is ExtensionRatingValue =>
  typeof value === 'number' && value >= 1 && value <= 5

export interface ExtensionScript {
  timestamp?: number
  name: string
  enabled: boolean
  script: string
  version?: string
  minClientVersion?: string
}

interface ExtensionVersion {
  scripts: ExtensionScript[]
}

// format '39.10.0'
type AppVersion = string

export type ExtensionRatings = {
  [userId: DataObjectId]: ExtensionRatingValue
}

export interface Extension {
  id: string
  author?: string
  ownerCompany: DataObjectId
  deleted: boolean
  nameRu: string
  nameEn: string
  descriptionRu: string
  descriptionEn: string

  // @dmitryvakarev убрал за ненадобностью, но в db это есть
  // link: string
  // apiKeys: ExtensionApiKey[]
  // stableVersion: number

  versions: {[appVersion: AppVersion]: ExtensionVersion}

  // Dynamic data objects that related to specific users or companies
  installs: {[companyId: DataObjectId]: InstalledExtension}
  ratings: ExtensionRatings

  // @dmitryvakarev: флаг, означающий не старые расширения типа mail-sync.js
  custom?: boolean

  platform?: boolean
  webPlatform?: boolean
  media?: ExtensionMedia
  dateReleased?: string
  tags: ExtensionTag[]
  instructionLink?: string
  keywords: string[]

  previews?: string[] // @deprecated use media[] instead
}

export type ExtensionRatingValue = 0 | 1 | 2 | 3 | 4 | 5

export interface ExtensionUserRating {
  extensionId: string
  companyId: DataObjectId
  userId: DataObjectId
  rating: ExtensionRatingValue
}

export interface ExtensionVersions {
  id: string
}

export type ExtensionTag = keyof typeof Lang.msg.extensions.tags

export const getExtensionTagTitle = (tag: ExtensionTag): string => {
  return Lang.msg.extensions.tags[tag] ?? tag
}

export type ExtensionMedia = (string | ExtensionMediaItem)[]

export interface ExtensionMediaItem {
  imageUrl: string
  videoUrl: string
}

export interface ExtensionManifest {
  ui: string | string[]
  service: string

  author?: string
  name?: string
  description?: string

  image?: string // @deprecated, use previews[] instead
  previews?: string[] // @deprecated, use media[] instead
  media?: ExtensionMedia

  // Used to display extensions that was released
  // less than 3 months ago as "New" in Extensions Gallery
  dateReleased?: string

  // This tags used in Extensions Gallery to filter extensions
  tags?: ExtensionTag[]

  instructionLink?: string

  // This keywords does not displays in the UI and used just
  // to enchance search expirience in Extensions Gallery
  keywords?: string[]

  // webplatform server stops sending the extension
  enabled?: boolean

  version: string // format '1.0.0'
  minClientVersion?: string // format '39.10.0'

  // @dmitryvakarev: логика такая, чтобы расширение появилось в вебе,
  // нужно выставить флаг в true сейчас это аналог enabled по умолчанию все
  // расширения копируются в коробку (поэтому флага для коробки нет) поэтому
  // в скрипте сбора коробки мы ручками удаляем ненужные расширения
  // чтобы они не тянулись в коробку
  inWeb?: boolean

  env: 'test' | undefined

  // определяет, будет ли расширение запущено при инициализации
  warmOnInit?: boolean
}

export interface ExtensionInfo {
  id: string
  author?: string
  name: string
  description: string

  // Indicates that the extension has ever been installed.
  installed: boolean

  // Indicates that the extension has ever been installed and currently enabled.
  enabled: boolean

  installsCount: number
  byUserId?: DataObjectId
  ts?: number
  dateReleased?: string
  media?: ExtensionMedia
  keywords: string[]
  tags: ExtensionTag[]
  instructionLink?: string
  ratings: ExtensionRatings
  ratingsCount: number

  // @deprecated: use media[] instead. Here is just for backward compatibility (desktop app).
  // Can be deleted after new version of desktop version will be released
  previews?: string[]
}

export const createExtensionInfo = (
  extension: Extension,
  companyId: DataObjectId,
  details: {lang: Language; ratings: ExtensionRatings}
): ExtensionInfo => {
  const install = extension.installs[companyId]

  return {
    id: extension.id,
    author: extension.author,
    name: details.lang === 'ru' ? extension.nameRu : extension.nameEn,
    description:
      details.lang === 'ru' ? extension.descriptionRu : extension.descriptionEn,
    enabled: !!install?.enabled,
    installed: Boolean(install),
    installsCount: Object.keys(extension.installs).length,
    byUserId: install?.byUserId,
    ts: install?.ts,
    media: extension.media,
    tags: extension.tags ?? [],
    instructionLink: extension.instructionLink ?? undefined,
    dateReleased: extension.dateReleased,
    keywords: extension.keywords ?? [],
    ratings: details.ratings ?? {},
    ratingsCount: Object.keys(details.ratings ?? {}).length,
    previews: extension.previews
  }
}

export interface InstalledExtension {
  id: string
  companyId: DataObjectId
  enabled: boolean // enabled in company by company admin
  byUserId?: DataObjectId
  ts?: number
  // these settings are only visible to extension owner (in REST API)
  // and administrators of the company, where extension was installed
  settings: {[key: string]: unknown}
}

export const isExtension = (obj: unknown): obj is Extension => {
  const ext = obj as Extension
  return !!ext?.id && !!ext.ownerCompany && !!ext.versions && !!ext.installs
}

export const defaultAppVersion = '39.10.0'

export const createInstalledExtension = (
  extension: Extension,
  companyId: DataObjectId
): InstalledExtension => {
  return {
    companyId,
    enabled: true,
    id: extension.id,
    settings: {}
  }
}

export const createNewExtension = (id: string): Extension => {
  return {
    id,
    author: '',
    ownerCompany: 'not-used' as DataObjectId,
    deleted: false,
    descriptionRu: '',
    descriptionEn: '',
    nameRu: '',
    nameEn: '',
    installs: {},
    versions: {},
    ratings: {},
    dateReleased: '',
    tags: [],
    keywords: []
  }
}

export interface PermissionHookRequest {
  event: string
  name: string
  extensionId: string
  companyId: string
  newObj: HubJSON
  oldObj: HubJSON | undefined
  extraFields?: PermissionExtraFields
  fromUserId: DataObjectId
}

export type PermissionHookResultItem = {
  result: 'ok' | 'error'
  allowed: boolean
  reason?: string
  error?: string
}

export type PermissionHookResult = {
  [requestId: string]: PermissionHookResultItem
}

export interface PermissionExtraFields {
  hasSubtasks?: boolean
  hasChecklists?: boolean
  isSubtasksCompleted?: boolean
  isChecklistsCompleted?: boolean
  hasDescription?: boolean
  description?: string
  stickers?: {[id: string]: {name: string; value: JSONLikeValue}}
  assignedUsers?: DataObjectId[]
}
